import { Col, Hr, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { P, Text } from "@amzn/stencil-react-components/text";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { getCookie } from "common/helpers/cookie-utils";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useProductFeedback } from "hooks/useProductFeedback";
import { AIForMeTool } from "lib/models/aiforme-models";
import { CatalogicalDomain, CatalogicalService, UserPermissions } from "lib/services";
import { FeedbackModelRecord } from "lib/services/catalogical-models";
import { forEach } from "lodash";
import React, { FC, useEffect, useState } from "react";

import { ProductReviewCard, ReviewType } from "./ProductReviewCard";

export interface ProductReviewsListProps {
  domain: CatalogicalDomain;
  taxonomyId: TaxonomyIdItem;
  refreshTrigger?: number; //triggers reviews refresh
}

export const ProductReviewsList: FC<ProductReviewsListProps> = (props) => {
  const {
    productFeedback: feedback,
    userFeedback,
    error,
    isLoading,
    doGetReviews,
    doGetUserReviews,
  } = useProductFeedback({ domain: props.domain });
  const username = UserPermissions.current.username ?? "";

  useEffect(() => {
    doGetReviews({ taxonomyId: props.taxonomyId });
    // not filtering by username for some reason
    // doGetUserReviews({ taxonomyId: props.taxonomyId, username: username });
  }, [props.refreshTrigger]);

  const isValidReview = (feedback: FeedbackModelRecord) => {
    return (
      feedback &&
      feedback.title &&
      feedback.title?.length > 0 &&
      feedback.categories &&
      feedback.categories.length > 0 &&
      feedback.content &&
      feedback.content?.length > 0
    );
  };

  const pushSelfReview = (reviewCards: JSX.Element[]) => {
    if (userFeedback.length > 0) {
      const userReview = userFeedback[0]; // get first review, when sorting is available we should get the latest
      reviewCards.push(<Hr key={`hr-${userReview.feedbackId}`} />);
      reviewCards.push(
        <ProductReviewCard
          key={`review-${userReview.feedbackId}`}
          content={userReview.content!}
          jobFamily={userReview.jobFamily!}
          title={userReview.title!}
          categories={userReview.categories!}
          isSelf={true}
        />
      );
      // Remove user review from feedback array to prevent duplication
      const userReviewIndex = feedback.findIndex((f) => f.feedbackId === userReview.feedbackId);
      if (userReviewIndex > -1) {
        feedback.splice(userReviewIndex, 1);
      }
    }
  };

  const generateReviewCards = () => {
    const reviewCards: JSX.Element[] = [];
    pushSelfReview(reviewCards);

    feedback.forEach((feedback: FeedbackModelRecord) => {
      if (isValidReview(feedback)) {
        reviewCards.push(<Hr key={`hr-${feedback.feedbackId}`} />);
        reviewCards.push(
          <ProductReviewCard
            key={`review-${feedback.feedbackId}`}
            content={feedback.content!}
            jobFamily={feedback.jobFamily!}
            title={feedback.title!}
            categories={feedback.categories!}
          />
        );
      }
    });
    if (reviewCards.length < 1) {
      reviewCards.push(NoReviews);
    }

    return reviewCards;
  };

  const renderLoadingComponent = () => {
    return (
      <Col alignItems="center" justifyContent="center" minHeight="400px">
        <Spinner size={SpinnerSize.Small} />
        <Text fontSize={"S100"}>{getString("productReviews.loading")}</Text>
      </Col>
    );
  };

  const NoReviews = (
    <View key="no-reviews" dataTestId="product-reviews-no-reviews">
      <Hr />
      <Text margin={"16px"}>{getString("productReviews.noReviews")}</Text>
    </View>
  );

  return (
    <Col dataTestId="product-reviews-list" height={"100%"} flex="1">
      {isLoading ? renderLoadingComponent() : generateReviewCards()}
    </Col>
  );
};
